import { Box, Flex, HStack } from '@chakra-ui/react'
import { mdiBookmarkOutline } from '@mdi/js'
import Icon from '@mdi/react'
import { useAtom } from 'jotai/react'
import React, { useMemo } from 'react'

import Copy from '@/components/copy'
import useColorModeValue from '@/hooks/useColorModeValue'
import { chatVisualsAtom, getVisualAgentInfo, shouldDisplayLabel } from '@/store/gigiChat/Visuals'
import { questionActionAtom } from '@/store/translate'
import { IMessageVisuals, IShortMessageObject } from '@/types/gigiChat/Chat'
import { IConversation } from '@/types/gigiChat/Conversation'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'

import Feedback from '../../feedback'
import CallToAction from './callToAction'
import Citations from './Citations'
import Disclaimer from './Disclaimer'
import FakeServerMessage from './fakeServerMessage'
import ConnectWithALiveAgent from './liveAgent/connectWithALiveAgent/ConnectWithALiveAgent'
import ExitLiveAgent from './liveAgent/exitLiveAgentButton/'
import MessageExport from './messageExport'
import ExitPromptLibrary from './promptLibrary/exitPromptLibraryButton/'
import ServerDocTranslationMessage from './serverDocTranslationMessage/ServerDocTranslationMessage'
import ServerMessage from './serverMessage'
import ServerTextTranslationMessage from './serverTextTranslationMessage/ServerTextTranslationMessage'
import Sources from './Sources'
import TypingDots from './typingDots/TypingDots'

const Message = React.memo(
    ({
        messages,
        item,
        index,
        settings,
        onNewQuestion,
    }: {
        messages: IShortMessageObject[] | IConversation[]
        item: IShortMessageObject | IConversation
        index: number
        settings: IConversationSettings | null
        onNewQuestion?: (question: string) => void
    }) => {
        const [chatVisuals, setChatVisuals] = useAtom<IMessageVisuals | null>(chatVisualsAtom)
        const [questionAction] = useAtom(questionActionAtom)

        //this method is to prevent multiple disconect buttons to be displayed ion the same time
        const getVisualDetails = () => {
            const currentVisual = chatVisuals?.existingPrompts?.filter((elem) => elem.userPromptId === Number(item.userPromptId))[0]
            if (currentVisual) {
                return currentVisual
            }

            const lastVisual = chatVisuals?.existingPrompts?.[chatVisuals.existingPrompts.length - 1]
            if (lastVisual?.isEnd === true) {
                const updatedVisuals = chatVisuals?.existingPrompts?.map((elem, idx) =>
                    idx === chatVisuals.existingPrompts.length - 1 ? { ...elem, isEnd: false } : elem,
                )
                if (chatVisuals && updatedVisuals) {
                    setChatVisuals({
                        ...chatVisuals,
                        existingPrompts: updatedVisuals,
                        nextPrompt: chatVisuals.nextPrompt,
                    })
                }
            }

            return {
                isStart:
                    messages[index - 1]?.settings?.processingModePromptLibrary?.metadata !==
                    item.settings?.processingModePromptLibrary?.metadata,
                isEnd: Boolean(messages[index + 1] === undefined),
                isConnected: true,
            }
        }

        const borderColor = useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-surface-dark)')

        const shouldDisplayMessageExport = useMemo(() => {
            return !(settings?.processingStrategy === 'Translation')
        }, [settings?.processingStrategy])

        const isWebSearch =
            item.settings?.processingStrategy === 'Writing' && item.settings?.processingStrategyWriting?.model === 'bing-grounding'

        if (!item.question) {
            return <FakeServerMessage item={item} key={index} />
        }

        const isPromptLibrary =
            item?.settings?.processingStrategy === 'Writing' && item.settings?.processingStrategyWriting.mode === 'promptLibrary'
        const isAgent = item?.settings?.processingStrategy === 'QA' && item.settings?.processingStrategyQa.mode === 'agent'
        const agentInfo = getVisualAgentInfo(item?.settings?.processingModeAgent?.name)

        // Temporary fix until they will sort them in BE
        // Currently are sorted from BE only on stream
        const sortedCitations = item.citations?.sort((a, b) => b.usedInCompletion - a.usedInCompletion)
        const groupMessages = isPromptLibrary || isAgent

        const visual = getVisualDetails()

        const isFirstMessageFromGroup = groupMessages && visual.isStart

        const isLastMessageFromGroup = (groupMessages && visual.isEnd) || !groupMessages
        const isConnected = groupMessages && visual.isConnected
        const sidebarColor = isAgent ? (isConnected ? agentInfo?.sidebarColorConnected : agentInfo?.sidebarColorDisconnected) : borderColor
        const showSidebar = isAgent ? agentInfo?.sidebarColorConnected && agentInfo?.sidebarColorDisconnected : false
        return (
            <Box
                key={item.userPromptId || item.question}
                borderLeft={showSidebar && groupMessages ? 'solid 2px' : undefined}
                borderLeftColor={showSidebar && groupMessages ? sidebarColor : undefined}
                pl={showSidebar && groupMessages ? '2' : undefined}
                mt={isFirstMessageFromGroup || !groupMessages ? '4' : undefined}
            >
                {isFirstMessageFromGroup && isPromptLibrary && (
                    <HStack>
                        <Icon path={mdiBookmarkOutline} size="16px" />
                        <Box fontSize={'xs'}>{item.settings?.processingModePromptLibrary?.label}</Box>
                    </HStack>
                )}

                {settings && settings?.processingStrategy !== 'Translation' && (
                    <ServerMessage
                        item={item}
                        question={item.question}
                        answer={item.answer}
                        displayAgentLabel={isFirstMessageFromGroup && shouldDisplayLabel(item.settings)}
                        isAgent={isAgent}
                        agentInfo={agentInfo}
                    />
                )}
                {settings &&
                    settings?.processingStrategy === 'Translation' &&
                    (questionAction === 'QTextTranslate' ||
                        (item.structuredAnswer && item.structuredAnswer.structureType === 'textTranslate')) && (
                        <ServerTextTranslationMessage
                            item={item}
                            isLastItem={messages.length - 1 === index}
                            question={item.question}
                            answer={item.answer}
                        />
                    )}
                {settings &&
                    settings?.processingStrategy === 'Translation' &&
                    (questionAction === 'QDocTranslate' ||
                        (item.structuredAnswer && item.structuredAnswer.structureType === 'docTranslate') ||
                        (item.questionAction && item.questionAction === 'QDocTranslate')) && (
                        <ServerDocTranslationMessage
                            item={item}
                            isLastItem={messages.length - 1 === index}
                            question={item.question}
                            answer={item.answer}
                        />
                    )}
                {item.structuredAnswer &&
                    settings &&
                    settings?.processingStrategy !== 'DocAnalysis' &&
                    settings?.processingStrategy !== 'Translation' &&
                    isLastMessageFromGroup && (
                        <CallToAction
                            structuredActions={item.structuredAnswer.callToAction?.action}
                            settings={settings}
                            onNewQuestion={onNewQuestion}
                            question={item.question}
                        />
                    )}
                {item.answer && !isAgent && <Disclaimer item={item} />}
                {!isWebSearch && <Citations citations={sortedCitations || []} />}
                {isWebSearch && <Sources citations={sortedCitations || []} />}
                {!item.isStreaming && item.answer && (!agentInfo || agentInfo?.showThumbsUpCopy) && (
                    <Flex alignItems="center" data-testid="chat-message-actions">
                        <Feedback userPromptId={item.userPromptId} externalFeedback={item.feedback || null} />
                        {shouldDisplayMessageExport && (
                            <MessageExport message={item.answer} userPromptId={item.userPromptId} mr="12px" size="18px" />
                        )}
                        <Copy message={item.answer} itemId={item.userPromptId} size="18px" />
                    </Flex>
                )}

                {(isAgent ||
                    settings?.processingStrategy === 'QA' ||
                    settings?.processingStrategy === 'Writing' ||
                    settings?.processingStrategy === 'DocAnalysis') &&
                    item.isStreaming &&
                    messages.length - 1 === index && <TypingDots />}

                {isLastMessageFromGroup && isAgent && <ExitLiveAgent isConnected={isConnected} item={item} agentInfo={agentInfo} />}

                {isLastMessageFromGroup && isPromptLibrary && <ExitPromptLibrary isConnected={isConnected} item={item} />}

                {item.structuredAnswer?.callToAction?.action[0].name === 'gigi' && <ConnectWithALiveAgent />}
            </Box>
        )
    },
)
export default Message
